import * as Collapsible from '@radix-ui/react-collapsible';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tooltip from '@radix-ui/react-tooltip';
import { ChevronLeft, ChevronRight, ChevronRight as ChevronRightIcon, Menu, X } from 'lucide-react';
import { createContext, useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { AnimatedCubeIcon } from '@/components/animated/animated-cube-icon';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/auth-context';
import { hasMatchingPermission, usePermissions } from '@/hooks/use-permissions';
import { cn } from '@/lib/utils';

export type NavItem = {
  name: string;
  icon?: React.ReactNode;
  pathname?: string;
  children?: NavItem[];
  code?: string; // Permission code for access control
};

// Sidebar context for collapsed state
interface SidebarContextValue {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

function useSidebarContext() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebarContext must be used within SidebarProvider');
  }
  return context;
}

const getPaddingClass = (depth: number, collapsed: boolean) => {
  if (collapsed) return '';
  switch (depth) {
    case 0:
      return '';
    case 1:
      return 'pl-5';
    case 2:
      return 'pl-8';
    default:
      return 'pl-11';
  }
};

interface Props {
  navigation: NavItem[];
  navbarTitle?: string;
  menuListItem: {
    name: string;
    onClick?: () => void;
    href?: string;
    icon?: React.ReactNode;
  }[];
}

function NavItemComponent({ item, depth = 0 }: { item: NavItem; depth?: number }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { collapsed } = useSidebarContext();
  const paddingLeft = getPaddingClass(depth, collapsed);

  const isActive = item.pathname === location.pathname;

  // For collapsed sidebar with children, don't show them
  if (collapsed && item.children && depth === 0) {
    return (
      <li>
        <Tooltip.Provider delayDuration={0}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <Link
                to={item.pathname ?? '#'}
                className={cn(
                  isActive ? 'bg-gray-50' : 'hover:bg-gray-50',
                  'group flex items-center justify-center rounded-md p-2 text-sm font-semibold leading-6 text-gray-700',
                )}
              >
                {item.icon}
              </Link>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="right"
                sideOffset={10}
                className="z-50 rounded-md bg-gray-900 px-3 py-1.5 text-sm text-white shadow-md"
              >
                {item.name}
                <Tooltip.Arrow className="fill-gray-900" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </li>
    );
  }

  // Collapsed mode - show only icon with tooltip
  if (collapsed && depth === 0) {
    return (
      <li>
        <Tooltip.Provider delayDuration={0}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <Link
                to={item.pathname ?? '#'}
                className={cn(
                  isActive ? 'bg-gray-50' : 'hover:bg-gray-50',
                  'group flex items-center justify-center rounded-md p-2 text-sm font-semibold leading-6 text-gray-700',
                )}
              >
                {item.icon}
              </Link>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="right"
                sideOffset={10}
                className="z-50 rounded-md bg-gray-900 px-3 py-1.5 text-sm text-white shadow-md"
              >
                {item.name}
                <Tooltip.Arrow className="fill-gray-900" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </li>
    );
  }

  // In collapsed mode, don't render nested items
  if (collapsed && depth > 0) {
    return null;
  }

  if (!item.children) {
    return (
      <li>
        <Link
          to={item.pathname ?? '#'}
          className={cn(
            isActive ? 'bg-gray-50' : 'hover:bg-gray-50',
            'group flex items-center gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700',
            paddingLeft,
          )}
        >
          {item.icon}
          {item.name}
        </Link>
      </li>
    );
  }

  return (
    <li>
      <Collapsible.Root open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center">
          <Link
            to={item.pathname ?? '#'}
            className={cn(
              isActive ? 'bg-gray-50' : 'hover:bg-gray-50',
              'flex flex-1 items-center gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700',
              paddingLeft,
            )}
          >
            {item.icon}
            {item.name}
          </Link>

          <Collapsible.Trigger asChild>
            <button type="button" className="p-2 rounded-md hover:bg-gray-50">
              <ChevronRightIcon
                className={cn(
                  isOpen ? 'rotate-90 text-gray-500' : 'text-gray-400',
                  'h-5 w-5 shrink-0 transition-transform',
                )}
              />
            </button>
          </Collapsible.Trigger>
        </div>

        <Collapsible.Content asChild>
          <ul className="mt-1">
            {item.children?.map((subItem) => (
              <NavItemComponent key={subItem.name} item={subItem} depth={depth + 1} />
            ))}
          </ul>
        </Collapsible.Content>
      </Collapsible.Root>
    </li>
  );
}

function filterByPermissions(items: NavItem[], permissions: string[]): NavItem[] {
  return items.reduce<NavItem[]>((acc, item) => {
    // If item has children, filter them recursively first
    if (item.children) {
      const filteredChildren = filterByPermissions(item.children, permissions);
      if (filteredChildren.length > 0) {
        acc.push({ ...item, children: filteredChildren });
      }
      return acc;
    }

    // Leaf item: if it has a code, check permission (prefix match)
    if (item.code && !hasMatchingPermission(item.code, permissions)) return acc;

    acc.push(item);
    return acc;
  }, []);
}

export function SideBar(props: Props) {
  const { navigation, menuListItem, navbarTitle } = props;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    // Initialize from localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebar-collapsed');
      return saved === 'true';
    }
    return false;
  });
  const { user } = useAuth();
  const { permissions } = usePermissions();
  const filteredNavigation = filterByPermissions(navigation, permissions);

  // Persist collapsed state
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(collapsed));
  }, [collapsed]);

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      {/* Mobile sidebar */}
      <Dialog.Root open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-gray-900/80 z-50 lg:hidden" />
          <Dialog.Content className="fixed inset-y-0 left-0 z-50 w-full max-w-xs bg-white lg:hidden">
            <div className="absolute right-0 top-0 -mr-12 pt-5">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="ml-1 flex h-10 w-10 items-center justify-center cursor-pointer"
                >
                  <X className="h-6 w-6 text-white" />
                </button>
              </Dialog.Close>
            </div>

            <div className="flex grow flex-col gap-y-5 overflow-y-auto px-6 pb-2">
              <Link to="/home">
                <div className="flex h-16 shrink-0 items-center">
                  <AnimatedCubeIcon size={25} />
                </div>
              </Link>
              <nav className="flex flex-1 flex-col">
                <ul className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul className="-mx-2 space-y-1">
                      {/* Force expanded mode in mobile */}
                      <SidebarContext.Provider value={{ collapsed: false, setCollapsed }}>
                        {filteredNavigation.map((item) => (
                          <NavItemComponent key={item.name} item={item} />
                        ))}
                      </SidebarContext.Provider>
                    </ul>
                  </li>
                </ul>
              </nav>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Static sidebar for desktop */}
      <div
        className={cn(
          'hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300',
          collapsed ? 'lg:w-16' : 'lg:w-72',
        )}
      >
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-3">
          <Link to="/home">
            <div
              className={cn('flex h-16 shrink-0 items-center', collapsed ? 'justify-center' : '')}
            >
              <AnimatedCubeIcon size={25} />
            </div>
          </Link>

          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className={cn('-mx-2 space-y-1', collapsed && 'mx-0')}>
                  {navbarTitle && !collapsed && (
                    <li>
                      <span className="font-medium text-muted-foreground text-xs">
                        {navbarTitle}
                      </span>
                    </li>
                  )}

                  {filteredNavigation.map((item) => (
                    <NavItemComponent key={item.name} item={item} />
                  ))}
                </ul>
              </li>

              {/* Collapse toggle button at bottom */}
              <li className="mt-auto mb-4">
                <button
                  type="button"
                  onClick={() => setCollapsed(!collapsed)}
                  className={cn(
                    'flex items-center gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 w-full cursor-pointer',
                    collapsed && 'justify-center',
                  )}
                >
                  {collapsed ? (
                    <ChevronRight className="h-5 w-5" />
                  ) : (
                    <>
                      <ChevronLeft className="h-5 w-5" />
                      <span>Recolher</span>
                    </>
                  )}
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Mobile top bar */}
      <div className="sticky top-0 z-40 flex items-center gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden">
        <button
          type="button"
          className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <span className="sr-only">Open sidebar</span>
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex-1 text-sm font-semibold leading-6 text-gray-900">Dashboard</div>

        {/* Mobile profile dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="relative flex rounded-full bg-white text-sm cursor-pointer focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
            >
              <Avatar className="size-8">
                <AvatarImage src={user?.avatar} alt="User avatar" />
                <AvatarFallback>{user?.name}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {menuListItem.map((item) => (
              <DropdownMenuItem key={item.name} onClick={item.onClick} asChild={!!item.href}>
                {item.href ? (
                  <Link to={item.href} className="flex items-center gap-3">
                    {item.icon}
                    {item.name}
                  </Link>
                ) : (
                  <span className="flex items-center gap-3 cursor-pointer">
                    {item.icon}
                    {item.name}
                  </span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </SidebarContext.Provider>
  );
}

// Export hook for other components to use
export function useSidebarCollapsed() {
  const context = useContext(SidebarContext);
  return context?.collapsed ?? false;
}
